import { motion } from "framer-motion";
import { SCENIC_EDITIONS, SCENIC_PETITE_SIZE_SUITE, PETITE_LIFESTYLE } from "@/lib/watchImages";
import WatchImage from "@/components/site/WatchImage";

export const TID_SCENIC_SECTION = "section-scenic-editions";
export const scenicEditionTid = (slug) => `scenic-edition-${slug}`;
export const scenicSizeSuiteTid = "scenic-size-suite";

export default function ScenicEditions() {
  return (
    <section
      id="scenic-editions"
      data-testid={TID_SCENIC_SECTION}
      className="relative py-28 sm:py-40 border-t border-[rgba(197,160,89,0.15)] overflow-hidden"
    >
      {/* Ambient backdrop: woman with CLEMENT on the course */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={PETITE_LIFESTYLE[2].src}
          alt=""
          className="w-full h-full object-cover object-right opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#14201a] via-[#14201a]/70 to-[#14201a]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14201a]/80 via-transparent to-[#14201a]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <span className="overline">The Scenic Editions</span>
          </div>
          <h2 className="font-serif-display text-[#F5F2EB] font-light text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
            Wear the course{" "}
            <span className="italic text-[#C5A059]">you grew up on</span>.
          </h2>
          <p className="text-[#A8A9AD] text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
            Scenic dials are hand-painted. The idea is simple — pick the
            landscape that means something to you (your home course, a
            family course, that one trip), and we put it on the dial.
            Same Swiss movement and the same complications, in stainless
            steel.
          </p>
        </motion.div>

        {/* Scenic Dial — men's */}
        <div className="mt-16">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="overline">Scenic Dial · 42 mm</div>
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]">
              Hand-finished men's landscape dials
            </span>
          </div>
          <div className="mt-6 flex flex-wrap justify-center">
            {SCENIC_EDITIONS.filter((w) => w.line === "Scenic Dial").map(
              (w, i) => (
                <ScenicCard key={w.slug} edition={w} index={i} widthClass="w-full md:w-1/2 lg:w-1/3" />
              ),
            )}
          </div>
        </div>

        {/* Scenic Petite — women's */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="overline">Scenic Petite · 28 · 30 · 32 mm</div>
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]">
              Wind &amp; stroke only · no clutter
            </span>
          </div>
          <div className="mt-6 flex flex-wrap justify-center">
            {SCENIC_EDITIONS.filter((w) => w.line === "Scenic Petite").map(
              (w, i) => (
                <ScenicCard key={w.slug} edition={w} index={i} widthClass="w-full md:w-1/2 lg:w-1/3" />
              ),
            )}
          </div>

          {/* Petite size suite — 28 / 30 / 32 mm wide feature */}
          <motion.figure
            data-testid={scenicSizeSuiteTid}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="mt-px relative bg-[#0C0F0A] border border-[rgba(197,160,89,0.18)] overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-px">
              <div className="lg:col-span-3 relative min-h-[420px] sm:min-h-[520px]">
                <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
                <WatchImage
                  src={SCENIC_PETITE_SIZE_SUITE.src}
                  alt="CLEMENT COURSE I Scenic Petite — 28, 30 and 32 mm size suite"
                  fallbackTitle={SCENIC_PETITE_SIZE_SUITE.title}
                  fallbackSub="28 · 30 · 32 mm"
                  loading="lazy"
                  testId="scenic-size-suite-image"
                />
              </div>
              <figcaption className="lg:col-span-2 p-8 sm:p-12 flex flex-col justify-center bg-[#0C0F0A]">
                <div className="overline">Petite Suite</div>
                <h4 className="font-serif-display text-3xl sm:text-4xl font-light text-[#F5F2EB] mt-4">
                  Three sizes,{" "}
                  <span className="italic text-[#C5A059]">same watch</span>.
                </h4>
                <p className="text-[#A8A9AD] text-sm sm:text-base leading-relaxed mt-5">
                  The Petite comes in 28, 30 and 32 mm so you can pick
                  what actually fits your wrist. Same painted dials, same
                  movement — just three case sizes.
                </p>
                <div className="hairline mt-8" />
                <div className="mt-5 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
                  <span>{SCENIC_PETITE_SIZE_SUITE.caption}</span>
                  <span>By allocation</span>
                </div>
              </figcaption>
            </div>
          </motion.figure>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]">
          <span>Hand-painted dials · Made one at a time</span>
          <a
            href="#waitlist"
            className="text-[#C5A059] hover:text-[#F5F2EB] transition-colors"
          >
            Send me your course →
          </a>
        </div>
      </div>
    </section>
  );
}

function ScenicCard({ edition: w, index, widthClass = "w-full md:w-1/2 lg:w-1/3" }) {
  return (
    <motion.figure
      data-testid={scenicEditionTid(w.slug)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
      className={`group relative bg-[#0C0F0A] p-6 sm:p-10 flex flex-col border border-[rgba(197,160,89,0.15)] -mt-px -ml-px box-border ${widthClass}`}
    >
      <div className="relative aspect-square w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
        <WatchImage
          src={w.front}
          alt={`CLEMENT COURSE I — ${w.name} ${w.line}`}
          fallbackTitle={w.name}
          fallbackSub={`${w.ref} · ${w.line}`}
          loading={index === 0 ? "eager" : "lazy"}
          className="transition-transform duration-700 group-hover:scale-105"
          testId={`scenic-${w.slug}-image`}
        />
      </div>
      <figcaption className="mt-8">
        <div className="overline mb-3">
          {w.ref} · {w.line}
        </div>
        <h3 className="font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] tracking-tight font-light leading-tight">
          {w.name}
        </h3>
        <p className="text-[#A8A9AD] text-sm sm:text-base leading-relaxed mt-4 max-w-md">
          {w.notes}
        </p>
        <div className="hairline mt-8" />
        <div className="mt-5 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
          <span>{w.metal}</span>
          <span>By allocation</span>
        </div>
      </figcaption>
    </motion.figure>
  );
}
